import React, { useEffect, useState } from "react";
import { Button, Grid, Header, Input, Message, Table } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { onSnapshot } from "firebase/firestore";
import { subMonths } from "date-fns";
import _ from "lodash";
import {
  dataFromSnapshot,
  fetchPartiesForUserFromDb,
  fetchTransactionsForUserFromDb,
  fetchDocsNeedingSignatureForUserFromDb,
} from "../../../app/firestore/firestoreService";
import {
  partyIsAgent,
  partyIsBuyerAgent,
  partyIsListingAgent,
  partyIsMyCoAgent,
  partyIsMyTc,
} from "../../../app/common/util/util";
import { openModal } from "../../../app/common/modals/modalSlice";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import TransactionNonLinkedListItem from "./TransactionNonLinkedListItem";

// Determine whether/how a party record represents a share that this agent can
// ONLY reach through the party/documents flow (i.e. what they'd see at
// /overviewParty) - either because they aren't a TransActioner user under
// that email (non-user agent), or because the share was never linked to their
// own transaction record. Returns the role this user plays on the share so
// the dashboard can label it (primary agent vs. co-agent/TC/assistant helping
// someone else), or null if this isn't a non-linked share at all.
function getNonLinkedShareMatch(party, email) {
  if (!party || !email) return null;

  if (party.email === email) {
    if (partyIsAgent(party.role)) {
      return !(party.isUser && party.isLinked) ? { type: "agent" } : null;
    }
    if (partyIsMyCoAgent(party.role) || partyIsMyTc(party.role)) {
      // "CoAgent (Mine)"/"TC (Mine)" can only be added when that person is
      // already a TransActioner subscriber (enforced when adding them), and
      // they get linked to the transaction directly at that point - so the
      // transaction owner is, by definition, already a TransActioner user
      // and this person already has access via the normal
      // Active/All/Complete/Archived (or TC) dashboards.
      return null;
    }
    // Not an agent-role party (e.g. this email is the client on the transaction) -
    // there is no "linked" concept for these, they only ever view via the party flow.
    return { type: "client" };
  }
  if (party.coAgent?.email === email) {
    // If the agent I'm supporting (the party's own agent, e.g. the Listing
    // Agent) is already a TransActioner user, I can already reach this
    // transaction via the normal Active/All/Complete/Archived tabs (as their
    // co-agent), so only surface it here when they are NOT a subscriber.
    return !party.isUser ? { type: "coAgent" } : null;
  }
  if (party.transactionCoordinator?.email === email) {
    return !(party.transactionCoordinator?.isUser && party.isLinked)
      ? { type: "transactionCoordinator" }
      : null;
  }
  if (party.assistant?.email === email) {
    // Assistants never get their own linked transaction, always via the party flow.
    return { type: "assistant" };
  }
  return null;
}

function sideLabelForRole(role) {
  if (partyIsBuyerAgent(role)) return "Buyer Agent";
  if (partyIsListingAgent(role)) return "Listing Agent";
  return role || "Agent";
}

// What to show in the "My Role" column for a given match.
// Note: "CoAgent (Mine)"/"TC (Mine)" never reach here - getNonLinkedShareMatch
// excludes them entirely, since the transaction owner is by definition
// already a TransActioner user in those cases.
function myRoleLabelFor(match, party) {
  const side = sideLabelForRole(party.role);
  const name = `${party.firstName || ""} ${party.lastName || ""}`.trim();

  switch (match.type) {
    case "agent":
      return side;
    case "coAgent":
      return `Co-Agent for ${name} (${side})`.trim();
    case "transactionCoordinator":
      return `TC for ${name} (${side})`.trim();
    case "assistant":
      return `Assistant to ${name} (${side})`.trim();
    default:
      return party.role || "";
  }
}

export default function TransactionNonLinkedDashboard() {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  // The parties/transactions queries below match against the Firebase Auth
  // email (auth.currentUser.email), which can differ in casing/value from the
  // profile document's email field - use the same source here so our
  // client-side matching lines up with what the query actually returned.
  const authEmail = useSelector((state) => state.auth.currentUser?.email);
  const isMobile = useMediaQuery({ query: "(max-width:850px)" });

  const [rawParties, setRawParties] = useState([]);
  const [transactionsForUser, setTransactionsForUser] = useState([]);
  const [docsNeedingSignature, setDocsNeedingSignature] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [underContractTransactions, setUnderContractTransactions] = useState([]);
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [completedTransactions, setCompletedTransactions] = useState([]);
  const [archivedTransactions, setArchivedTransactions] = useState([]);

  // Subscribe directly (rather than via useFirestoreCollection) so any error -
  // including permission-denied - is visible here instead of being silently
  // swallowed, since a swallowed error would otherwise look identical to
  // "no non-linked transactions".
  useEffect(() => {
    if (!authEmail) return;
    const unsubscribe = onSnapshot(
      fetchPartiesForUserFromDb(),
      (snapshot) => {
        setRawParties(snapshot.docs.map((doc) => dataFromSnapshot(doc)));
      },
      (error) => {
        console.error("TransactionNonLinkedDashboard: parties query failed", error);
        setLoadError(error);
        setRawParties([]);
      }
    );
    return () => unsubscribe();
  }, [authEmail]);

  useEffect(() => {
    if (!authEmail) return;
    const unsubscribe = onSnapshot(
      fetchTransactionsForUserFromDb(),
      (snapshot) => {
        setTransactionsForUser(snapshot.docs.map((doc) => dataFromSnapshot(doc)));
      },
      (error) => {
        console.error(
          "TransactionNonLinkedDashboard: transactions query failed",
          error
        );
        setLoadError(error);
        setTransactionsForUser([]);
      }
    );
    return () => unsubscribe();
  }, [authEmail]);

  useEffect(() => {
    if (!authEmail) return;
    const unsubscribe = onSnapshot(
      fetchDocsNeedingSignatureForUserFromDb(),
      (snapshot) => {
        setDocsNeedingSignature(snapshot.docs.map((doc) => dataFromSnapshot(doc)));
      },
      (error) => {
        // Non-critical - just skip the "needs signature" indicator on error.
        console.error(
          "TransactionNonLinkedDashboard: docsNeedingSignature query failed",
          error
        );
        setDocsNeedingSignature([]);
      }
    );
    return () => unsubscribe();
  }, [authEmail]);

  useEffect(() => {
    if (authEmail) {
      const currentUserEmail = authEmail;
      const sixMonthsAgo = subMonths(new Date(), 6);

      const matchedParties = rawParties
        .map((party) => ({
          party,
          match: getNonLinkedShareMatch(party, currentUserEmail),
        }))
        .filter((entry) => entry.match);

      // Only keep the first non-linked match per transaction
      const seenTransactionIds = new Set();
      const uniqueMatches = matchedParties.filter((entry) => {
        if (seenTransactionIds.has(entry.party.transactionId)) return false;
        seenTransactionIds.add(entry.party.transactionId);
        return true;
      });

      let underContract = [];
      let active = [];
      let completed = [];
      let archived = [];

      uniqueMatches.forEach(({ party, match }) => {
        const transaction = transactionsForUser.filter(
          (trans) => trans.id === party.transactionId
        )[0];
        if (!transaction) return;

        // Only show transactions updated within the last 6 months.
        const updatedAtDate = transaction.updatedAt
          ? new Date(transaction.updatedAt)
          : null;
        if (!updatedAtDate || updatedAtDate < sixMonthsAgo) return;

        const partyWithTransaction = _.cloneDeep(party);
        partyWithTransaction["address"] = transaction.address || {};
        partyWithTransaction["agentFirstName"] =
          transaction.agentFirstName || transaction.agentProfile?.firstName || "";
        partyWithTransaction["agentLastName"] =
          transaction.agentLastName || transaction.agentProfile?.lastName || "";
        partyWithTransaction["agentRepresents"] = transaction.agentRepresents || "";
        partyWithTransaction["client"] = transaction.client || {};
        partyWithTransaction["clientSecondary"] = transaction.clientSecondary || {};
        partyWithTransaction["clientThird"] = transaction.clientThird || {};
        partyWithTransaction["mlsNumbers"] = transaction.mlsNumbers || [];
        partyWithTransaction["title"] = transaction.title || "";
        partyWithTransaction["transactionPic"] = transaction.pic || "";
        partyWithTransaction["status"] = transaction.status || "";
        partyWithTransaction["updatedAt"] = transaction.updatedAt || null;
        partyWithTransaction["closingDateTime"] = transaction.closingDateTime || null;
        partyWithTransaction["myRoleLabel"] = myRoleLabelFor(match, party);

        const docsNeedingSigning =
          docsNeedingSignature?.filter(
            (doc) =>
              doc.transactionId === transaction.id &&
              doc.signingRequestedFor &&
              doc.signingRequestedFor.includes(currentUserEmail) &&
              (doc.status === "Awaiting Signature" || doc.status === "In Progress") &&
              !doc.signingComplete
          ) || [];
        partyWithTransaction["needsSignatures"] = docsNeedingSigning.length > 0;

        if (transaction.status === "Under Contract") {
          underContract.push(partyWithTransaction);
        } else if (
          transaction.status === "Active Buyer" ||
          transaction.status === "Active Listing"
        ) {
          active.push(partyWithTransaction);
        } else if (transaction.status === "Complete") {
          completed.push(partyWithTransaction);
        } else if (transaction.status === "Archived") {
          archived.push(partyWithTransaction);
        }
      });

      const sortByLastUpdated = (a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt) : new Date(0);
        const dateB = b.updatedAt ? new Date(b.updatedAt) : new Date(0);
        return dateB - dateA;
      };
      const sortByClosingDate = (a, b) => {
        const dateA = a.closingDateTime ? new Date(a.closingDateTime) : new Date(0);
        const dateB = b.closingDateTime ? new Date(b.closingDateTime) : new Date(0);
        return dateB - dateA;
      };

      setUnderContractTransactions(underContract.sort(sortByLastUpdated));
      setActiveTransactions(active.sort(sortByLastUpdated));
      setCompletedTransactions(completed.sort(sortByClosingDate));
      setArchivedTransactions(archived.sort(sortByLastUpdated));
    }
    setLoaded(true);
  }, [rawParties, transactionsForUser, docsNeedingSignature, authEmail]);

  const filterTransactions = (transactions) => {
    if (!searchTerm.trim()) return transactions;
    const term = searchTerm.toLowerCase();
    return transactions.filter((party) => {
      const fields = [
        party.address?.street,
        party.address?.city,
        party.address?.state,
        party.address?.zipCode,
        party.agentFirstName,
        party.agentLastName,
        party.agentRepresents,
        party.title,
      ];
      return fields.some((field) => field?.toString().toLowerCase().includes(term));
    });
  };

  const renderTable = (transactions, title, showClosingDate = false) => {
    const filteredTransactions = filterTransactions(transactions || []);
    if (filteredTransactions.length === 0) return null;

    return (
      <div className="medium bottom margin">
        <Header size="medium" color="blue" className="small bottom margin">
          {title}
        </Header>
        <Table compact className="mini top margin">
          <Table.Header className="mobile hidden">
            <Table.Row className="small-header">
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell>Address</Table.HeaderCell>
              <Table.HeaderCell>My Role</Table.HeaderCell>
              <Table.HeaderCell>Side</Table.HeaderCell>
              <Table.HeaderCell>Agent First Name</Table.HeaderCell>
              <Table.HeaderCell>Agent Last Name</Table.HeaderCell>
              <Table.HeaderCell>Last Updated</Table.HeaderCell>
              {showClosingDate && <Table.HeaderCell>Closing Date</Table.HeaderCell>}
              <Table.HeaderCell>Needs Signatures</Table.HeaderCell>
              <Table.HeaderCell></Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredTransactions.map((party) => (
              <TransactionNonLinkedListItem
                party={party}
                key={party.id}
                showClosingDate={showClosingDate}
              />
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const totalCount =
    underContractTransactions.length +
    activeTransactions.length +
    completedTransactions.length +
    archivedTransactions.length;

  if (!loaded) return <LoadingComponent content="Loading transactions..." />;

  return (
    <div className="main-page-wrapper">
      <Grid stackable>
        <Grid.Column computer={5} tablet={16}>
          <Input
            type="text"
            fluid
            size="small"
            placeholder="Search by agent name or address"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          ></Input>
        </Grid.Column>
        <Grid.Column computer={5} tablet={8}>
          <Button.Group fluid size="small">
            <Button as={Link} to="/transactions">
              Active
            </Button>
            <Button as={Link} to="/transactionsAll">
              All
            </Button>
            <Button as={Link} to="/transactionsComplete">
              Complete
            </Button>
            <Button as={Link} to="/transactionsArchived">
              Archived
            </Button>
            <Button active as={Link} to="">
              Non-Linked
            </Button>
          </Button.Group>
        </Grid.Column>
        <Grid.Column computer={4} tablet={4}>
          {(currentUserProfile.role === "agent" ||
            currentUserProfile.role === "assistant") && (
            <Button
              color="blue"
              to="#"
              floated={isMobile ? null : "right"}
              icon="plus"
              size="small"
              className={isMobile ? "fluid" : null}
              onClick={() =>
                dispatch(
                  openModal({
                    modalType: "TransactionForm",
                  })
                )
              }
              content="New Transaction"
            />
          )}
        </Grid.Column>
        <Grid.Column computer={16}>
          <h3>Non-Linked Transactions</h3>
          {loadError && (
            <Message negative>
              <Message.Header>Unable to load non-linked transactions</Message.Header>
              <p>{loadError.code ? `${loadError.code}: ` : ""}{loadError.message}</p>
            </Message>
          )}
          {totalCount !== 0 ? (
            <>
              <p>
                <i>
                  Recent non-linked transactions, including transactions where
                  you are a co-agent or TC for an agent who is not a
                  TransActioner subscriber.
                </i>
              </p>
              {renderTable(underContractTransactions, "Under Contract", true)}
              {renderTable(activeTransactions, "Active Transactions")}
              {renderTable(completedTransactions, "Completed", true)}
              {renderTable(archivedTransactions, "Archived")}
            </>
          ) : (
            !loadError && (
              <Message>
                You do not have any recent unlinked transactions or transactions where you are co-agent/TC/assistant for an agent who is not a TransActioner subscriber.
                <br/>Use the other menu options to view your transactions.
              </Message>
            )
          )}
        </Grid.Column>
      </Grid>
    </div>
  );
}
